import React, { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";

import "./content.component.styles.scss";
import { HomePage } from "../../pages";
import { SERVICES } from "../../../content/services";

// Eager-load the home route (LCP target).
// Lazy-load the rest so the initial bundle stays small — each route
// becomes its own chunk, fetched only when visited.
const AboutPage = lazy(() => import("../../pages/about-page"));
const BookingPage = lazy(() => import("../../pages/booking-page"));
const ContactPage = lazy(() => import("../../pages/contact-page"));
const TreatmentsPage = lazy(() => import("../../pages/treatments-page"));
const GiftCardPage = lazy(() => import("../../pages/gift-card-page"));
const ThankYouPage = lazy(() => import("../../pages/thank-you-page"));
const NotFoundPage = lazy(() => import("../../pages/not-found-page"));

// Location landing pages (geo-SEO)
const FirenzePage = lazy(
  () => import("../../pages/location-page/firenze-page.component"),
);
const SestoPage = lazy(
  () => import("../../pages/location-page/sesto-page.component"),
);
const ToscanaPage = lazy(
  () => import("../../pages/location-page/toscana-page.component"),
);

// Journal
const JournalIndex = lazy(() =>
  import("../../pages/journal-page").then((m) => ({ default: m.JournalIndex })),
);
const ArticlePage = lazy(() =>
  import("../../pages/journal-page").then((m) => ({ default: m.ArticlePage })),
);

// Service landing pages (intent-SEO) — same component, different content
const ServicePage = lazy(() => import("../../pages/service-page"));

// Legal pages (GDPR / Garante della Privacy)
const PrivacyPage = lazy(() =>
  import("../../pages/legal-page").then((m) => ({ default: m.PrivacyPage })),
);
const CookiePolicyPage = lazy(() =>
  import("../../pages/legal-page").then((m) => ({ default: m.CookiePolicyPage })),
);

const RouteFallback: React.FC = () => (
  <div
    className="lvs-content__fallback"
    role="status"
    aria-live="polite"
    style={{ minHeight: "50vh" }}
  />
);

const localizedRoutes = (prefix: string) => (
  <React.Fragment key={prefix || "root"}>
    <Route path={`${prefix}/`} element={<HomePage />} />
    <Route path={`${prefix}/about`} element={<AboutPage />} />
    <Route path={`${prefix}/booking`} element={<BookingPage />} />
    <Route path={`${prefix}/contact`} element={<ContactPage />} />
    <Route path={`${prefix}/treatments`} element={<TreatmentsPage />} />
    <Route path={`${prefix}/gift-card`} element={<GiftCardPage />} />

    {/* Location landing pages */}
    <Route path={`${prefix}/massaggi-firenze`} element={<FirenzePage />} />
    <Route
      path={`${prefix}/massaggi-sesto-fiorentino`}
      element={<SestoPage />}
    />
    <Route path={`${prefix}/massaggi-toscana`} element={<ToscanaPage />} />

    {/* Journal */}
    <Route path={`${prefix}/journal`} element={<JournalIndex />} />
    <Route path={`${prefix}/journal/:slug`} element={<ArticlePage />} />

    {/* Service landing pages — generated from content map */}
    {Object.entries(SERVICES).map(([slug, content]) => (
      <Route
        key={`${prefix}/${slug}`}
        path={`${prefix}/${slug}`}
        element={<ServicePage content={content} />}
      />
    ))}

    {/* Legal */}
    <Route path={`${prefix}/privacy`} element={<PrivacyPage />} />
    <Route path={`${prefix}/cookie-policy`} element={<CookiePolicyPage />} />
  </React.Fragment>
);

const Content: React.FC = () => {
  return (
    <div className="lvs-content">
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Italian — root paths (default language) */}
          {localizedRoutes("")}
          {/* English — /en prefix */}
          {localizedRoutes("/en")}

          {/* Post-payment return pages (SumUp redirect_url). Slugs differ per
              language, so they live outside the prefix-mirrored routes. */}
          <Route path="/grazie" element={<ThankYouPage />} />
          <Route path="/en/thank-you" element={<ThankYouPage />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </div>
  );
};

export default Content;
